import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { ChevronUp } from 'lucide-react';
import { useIsMobile } from '@/hooks/use-mobile';

interface ScrollToTopProps {
  threshold?: number;
  className?: string;
}

const ScrollToTop = ({ threshold = 150, className = '' }: ScrollToTopProps) => {
  const [isVisible, setIsVisible] = useState(false);
  const [isScrolling, setIsScrolling] = useState(false);
  const isMobile = useIsMobile();

  useEffect(() => {
    const getScrollContainer = () => {
      return document.querySelector('#main') || window;
    };

    const toggleVisibility = () => {
      if (window.pageYOffset > threshold) {
        setIsVisible(true);
      } else {
        setIsVisible(false);
      }
    };

    // Throttle scroll events for better performance
    let ticking = false;
    const handleScroll = () => {
      if (!ticking) {
        requestAnimationFrame(() => {
          toggleVisibility();
          ticking = false;
        });
        ticking = true;
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    
    // Check initial scroll position
    toggleVisibility();

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [threshold]);

  const scrollToTop = () => {
    setIsScrolling(true);
    
    
    // Also scroll window as fallback
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: 'smooth'
    });

    // Reset scrolling state after animation completes
      setIsScrolling(false);
  };

  if (!isVisible) {
    return null;
  }

  return (
    <Button
      onClick={scrollToTop}
      disabled={isScrolling}
      variant="secondary"
      size="icon"
      className={`
        fixed z-50 transition-all duration-300 ease-in-out
        shadow-lg hover:shadow-xl
        bg-primary/80 hover:bg-background
        border border-border/50 hover:border-border
        backdrop-blur-sm
        hover:scale-105 active:scale-95
        ${isMobile 
          ? 'bottom-4 right-4 h-10 w-10' 
          : 'bottom-6 right-6 h-11 w-11'
        } 
        opacity-100
        ${className}
      `}
      style={{
        borderRadius: '50%',
      }}
      aria-label="Scroll to top"
    >
      <ChevronUp className={`${isMobile ? 'h-4 w-4' : 'h-5 w-5'} transition-transform duration-200`} />
    </Button>
  );
};

export default ScrollToTop;